/**
 * Le relevé bancaire en PDF, lu comme un relevé camt.053.
 *
 * <p>Beaucoup de patrons n'ont que le PDF de leur banque (UBS, PostFinance,
 * Raiffeisen, BCGE, BCV, Banque Migros, Valiant, Revolut…) : son texte est
 * relu ligne par ligne, et chaque mouvement prend la forme que donne
 * parseCamt053 ({date, montant, sens, label, ref}) — le rapprochement qui suit
 * ne voit pas la différence.
 *
 * <p>Le sens d'un montant (sortie ou entrée) se décide, dans l'ordre :
 * <ol>
 *   <li>par sa colonne, quand l'en-tête « Débit / Crédit » (ou « Belastung /
 *       Gutschrift », « Soll / Haben », « Addebito / Accredito », « Money out /
 *       Money in ») est trouvé ;
 *   <li>par son signe (-120.00 ou 120.00-), quand le relevé en porte ;
 *   <li>par le solde : entre deux soldes connus, la seule combinaison
 *       d'entrées et de sorties qui passe de l'un à l'autre.
 * </ol>
 * Sans aucun de ces indices, la colonne des mouvements déjà classés, puis les
 * mots du libellé (« Virement de », « Achat »…) ; à défaut, le sens reste « à
 * vérifier » (`?`) : ni une sortie inventée, ni une entrée qui marquerait une
 * facture payée à tort.
 */

import { banqueDuTexte } from './banque-releve';
import { _mod10, _mod97 } from './swiss-refs';

/** Largeur supposée d'un caractère quand le texte vient sans positions. */
const LARGEUR_CAR = 6;

/** Pages lues au plus : un relevé de l'année tient largement dedans. */
const PAGES_MAX = 60;

/** Lignes de libellé ajoutées au plus à un mouvement (adresse, communication…). */
const SUITES_MAX = 6;

/** Le sens d'un mouvement que le relevé ne dit pas : au patron de le dire. */
export const SENS_A_VERIFIER = '?';

/** Le texte en minuscules et sans accents, caractère pour caractère (mêmes positions). */
const enMinuscules = (s) =>
  [...String(s)]
    .map((c) => {
      const b = c.toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '');
      return b.length === c.length ? b : c.length === 1 ? c : ' '.repeat(c.length);
    })
    .join('');

// Les dates suisses : 05.01.2026, 05.01.26, 05/01/2026 ; ISO : 2026-01-05.
const RE_DATE = /(?<![\d./])(\d{1,2})([./])(\d{1,2})\2(\d{4}|\d{2})(?![\d/])/g;
const RE_DATE_ISO = /(?<![\d.-])(\d{4})-(\d{2})-(\d{2})(?!\d)/g;

// Les mois écrits en toutes lettres, en français, allemand, italien et anglais.
// Les mois en français, anglais, allemand, italien, espagnol, portugais,
// néerlandais, albanais, croate/serbe/bosniaque, polonais, turc, roumain,
// tchèque et scandinave : le relevé d'une banque de l'étranger se lit comme
// celui d'une banque suisse. Les dates en chiffres se lisent partout.
const MOIS = [
  [
    'janvier',
    'janv',
    'january',
    'januar',
    'jan',
    'gennaio',
    'gen',
    'enero',
    'ene',
    'janeiro',
    'januari',
    'janar',
    'sijecanj',
    'styczen',
    'stycznia',
    'ocak',
    'ianuarie',
    'leden',
    'ledna',
    'janvar',
  ],
  [
    'fevrier',
    'fevr',
    'fev',
    'february',
    'februar',
    'febbraio',
    'feb',
    'febrero',
    'fevereiro',
    'februari',
    'shkurt',
    'veljaca',
    'luty',
    'lutego',
    'subat',
    'februarie',
    'unor',
    'unora',
  ],
  [
    'mars',
    'march',
    'marz',
    'marzo',
    'mar',
    'marco',
    'maart',
    'ozujak',
    'marzec',
    'marca',
    'mart',
    'martie',
    'brezen',
    'brezna',
  ],
  [
    'avril',
    'avr',
    'april',
    'aprile',
    'apr',
    'abril',
    'abr',
    'prill',
    'travanj',
    'kwiecien',
    'kwietnia',
    'nisan',
    'aprilie',
    'duben',
    'dubna',
  ],
  [
    'mai',
    'may',
    'maggio',
    'mag',
    'mayo',
    'maio',
    'mei',
    'maj',
    'svibanj',
    'maja',
    'mayis',
    'kveten',
    'kvetna',
  ],
  [
    'juin',
    'june',
    'juni',
    'jun',
    'giugno',
    'giu',
    'junio',
    'junho',
    'qershor',
    'lipanj',
    'czerwiec',
    'czerwca',
    'haziran',
    'iunie',
    'cerven',
    'cervna',
  ],
  [
    'juillet',
    'juil',
    'july',
    'juli',
    'jul',
    'luglio',
    'lug',
    'julio',
    'julho',
    'korrik',
    'srpanj',
    'lipiec',
    'lipca',
    'temmuz',
    'iulie',
    'cervenec',
    'cervence',
  ],
  [
    'aout',
    'august',
    'agosto',
    'aug',
    'ago',
    'augustus',
    'gusht',
    'kolovoz',
    'sierpien',
    'sierpnia',
    'agustos',
    'avgust',
    'srpen',
    'srpna',
    'augusti',
  ],
  [
    'septembre',
    'sept',
    'september',
    'settembre',
    'sep',
    'set',
    'septiembre',
    'setembro',
    'shtator',
    'rujan',
    'wrzesien',
    'wrzesnia',
    'eylul',
    'septembrie',
    'zari',
    'septembar',
  ],
  [
    'octobre',
    'october',
    'oktober',
    'ottobre',
    'oct',
    'okt',
    'ott',
    'octubre',
    'outubro',
    'out',
    'tetor',
    'pazdziernik',
    'pazdziernika',
    'ekim',
    'octombrie',
    'rijen',
    'rijna',
    'oktobar',
  ],
  [
    'novembre',
    'november',
    'nov',
    'noviembre',
    'novembro',
    'nentor',
    'studeni',
    'listopada',
    'kasim',
    'noiembrie',
    'novembar',
  ],
  [
    'decembre',
    'december',
    'dezember',
    'dicembre',
    'dec',
    'dez',
    'dic',
    'diciembre',
    'dezembro',
    'dhjetor',
    'prosinac',
    'grudzien',
    'grudnia',
    'aralik',
    'decembrie',
    'prosinec',
    'prosince',
    'decembar',
  ],
];
const NOM_MOIS = MOIS.flat()
  .sort((a, b) => b.length - a.length)
  .join('|');
// « 1 sept. 2026 », « 1. September 2026 », « 01 Sep 2026 », « 12 de marzo de 2026 ».
const RE_DATE_JOUR_MOIS = new RegExp(
  '(?<![\\w.])(\\d{1,2})\\.?\\s?(?:de\\s)?(' +
    NOM_MOIS +
    ')(?![a-z])\\.?,?\\s(?:de\\s)?(\\d{4})(?!\\d)',
  'g',
);
// « Sep 1, 2026 ».
const RE_DATE_MOIS_JOUR = new RegExp(
  '(?<![\\w])(' + NOM_MOIS + ')(?![a-z])\\.?\\s(\\d{1,2}),?\\s(\\d{4})(?!\\d)',
  'g',
);
const numeroDuMois = (nom) => MOIS.findIndex((noms) => noms.includes(nom)) + 1;

/** Les dates d'une ligne, chacune avec sa place : {iso, index, longueur}. */
function trouveDates(texte) {
  const bas = enMinuscules(texte);
  const dates = [];
  const prend = (index, longueur, iso) => {
    if (!iso) return;
    if (dates.some((d) => index < d.index + d.longueur && d.index < index + longueur)) return;
    dates.push({ iso, index, longueur });
  };
  for (const m of bas.matchAll(RE_DATE)) prend(m.index, m[0].length, dateIso(m[1], m[3], m[4]));
  for (const m of bas.matchAll(RE_DATE_ISO)) prend(m.index, m[0].length, dateIso(m[3], m[2], m[1]));
  for (const m of bas.matchAll(RE_DATE_JOUR_MOIS))
    prend(m.index, m[0].length, dateIso(m[1], String(numeroDuMois(m[2])), m[3]));
  for (const m of bas.matchAll(RE_DATE_MOIS_JOUR))
    prend(m.index, m[0].length, dateIso(m[2], String(numeroDuMois(m[1])), m[3]));
  return dates.sort((a, b) => a.index - b.index);
}

// Les montants : 1'234.50, 1’234.50, 1 234.50, 1,234.50 (anglais), 1.234,50
// (allemand), -120.00, 120.00-. Le signe collé au chiffre seulement : « Coop
// - 12.00 » n'est pas une sortie de 12.–.
const RE_MONTANT =
  /(?<![\d.,'’])([-−+])?(\d{1,3}(?:['’\u00a0\u202f ]\d{3})+|\d{1,3}(?:,\d{3})+(?=\.\d{2})|\d{1,3}(?:\.\d{3})+(?=,\d{2})|\d+)[.,](\d{2})(?![\d%])(-|−)?(?![\d%])/g;

// Les devises posées à côté d'un montant : elles ne font pas partie du libellé.
const RE_DEVISE = /\b(?:CHF|EUR|USD|GBP)\b/g;
const DEVISE_AVANT = /(CHF|EUR|USD|GBP)\s?$/;
const DEVISE_APRES = /^\s?(CHF|EUR|USD|GBP)\b(?!\s?[-−+]?\d)/;

// Les mots des colonnes, en français, allemand, italien et anglais.
const COL_DEBIT =
  /^(?:d[ée]bits?|d[ée]bito|belastung(?:en)?|lastschrift(?:en)?|soll|addebit[oi]|retraits?|sorties?|withdrawals?|ausgang|ausgänge|cargos?|gastos?|salidas?)$/i;
const COL_CREDIT =
  /^(?:cr[ée]dits?|cr[ée]dito|gutschrift(?:en)?|haben|accredit[oi]|versements?|entr[ée]es?|deposits?|eingang|eingänge|abonos?|ingresos?|entradas?)$/i;
const COL_SOLDE = /^(?:soldes?|saldo|balance|kontostand)$/i;
// Les colonnes en deux mots : « Money out / Money in », « Paid out / Paid in ».
const COL_DEUX_MOTS = /\b(?:money|paid)\s+(out|in)\b/gi;
// Le mot d'une colonne de dates ou de solde : un en-tête long en a toujours un.
const COL_AUTRE =
  /^(?:date|datum|data|valeur|valuta|value|booking|buchung|soldes?|saldo|balance)$/i;

// Un solde (reporté, ancien, nouveau, final…) : il donne le solde courant, ce
// n'est pas un mouvement.
const RE_SOLDE =
  /^(?:(?:nouveau|ancien|alter|neuer|neues|nuovo|vecchio|new|old|opening|closing|final)\s+)?(?:soldes?|saldo|saldovortrag|report|übertrag|uebertrag|vortrag|kontostand|riporto|balance)\b/i;
// Les mots qui accompagnent un solde ; un autre mot (« Solde travaux villa »)
// dit un mouvement, quand la ligne est datée.
const MOTS_DU_SOLDE =
  /^(?:soldes?|saldo|saldovortrag|report|reporte|à|a|nouveau|ancien|alter|neuer|neues|nuovo|vecchio|new|old|opening|closing|final|initial|precedent|au|du|de|en|votre|faveur|zu|ihren|gunsten|per|al|at|chf|eur|kontostand|riporto|balance|übertrag|uebertrag|vortrag|brought|carried|forward|:|-)$/i;
// Un total de page ou du relevé : ni un mouvement, ni un solde.
const RE_TOTAL = /^(?:sous-)?(?:totale?|totaux|totali|summe|zwischensumme|umsatztotal)\b/i;
// Un numéro de page : « Page 2 / 4 », « Seite 2 von 4 », « 2/4 ».
const RE_PAGE = /^(?:page|seite|pagina)\s*\d+\b|^\d+\s*\/\s*\d+$/i;

// Les mots d'un libellé qui disent une entrée, en dernier recours (« Ordre de
// versement » ou « Paiement » disent aussi bien une sortie). Lus sans accents.
const MOTS_ENTREE =
  /\b(?:gutschrift|bonification|accredito|zahlungseingang|eingang|virement recu|virement de|versement de|avis de credit|credit en votre faveur|remboursement|ruckerstattung|payment from|(?<!cartes? de )credit)\b/;
// Ceux qui disent une sortie : un achat, un retrait, un prélèvement, des frais.
const MOTS_SORTIE =
  /\b(?:achat|einkauf|retrait|bancomat|postomat|prelevement|lsv|recouvrement|lastschrift|ordre e-banking|ordre de paiement|ordre permanent|dauerauftrag|frais|cotisation|paiement (?:carte|maestro|twint)|envoi d.argent|debit direct|belastung|zahlung an|payment to|card payment)\b/;

/** Le sens que disent les mots d'un libellé, ou null s'ils ne disent rien de sûr. */
function sensDesMots(label) {
  const s = enMinuscules(label);
  const entree = MOTS_ENTREE.test(s);
  const sortie = MOTS_SORTIE.test(s);
  if (entree === sortie) return null;
  return entree ? 'CRDT' : 'DBIT';
}

/**
 * Le texte d'une ligne, avec la position de chaque caractère : les morceaux
 * posés côte à côte par le PDF, séparés d'une espace, ou de deux quand un vide
 * de colonne les sépare (un montant ne s'étend jamais par-dessus).
 */
function aplatit(morceaux) {
  let texte = '';
  const debuts = [];
  const fins = [];
  let finPrec = null;
  let carPrec = LARGEUR_CAR;
  const ajoute = (c, debut, fin) => {
    texte += c;
    debuts.push(debut);
    fins.push(fin);
  };
  const tries = morceaux
    .filter((m) => m && typeof m.str === 'string' && m.str.trim())
    .sort((a, b) => (Number(a.x) || 0) - (Number(b.x) || 0));
  for (const m of tries) {
    const str = m.str;
    const x = Number(m.x) || 0;
    const car = m.largeur > 0 ? m.largeur / str.length : LARGEUR_CAR;
    if (finPrec !== null) {
      const ecart = x - finPrec;
      const n =
        ecart > 1.5 * Math.max(car, carPrec) ? 2 : ecart > 0.2 * Math.min(car, carPrec) ? 1 : 0;
      for (let i = 0; i < n; i++)
        ajoute(' ', finPrec + (ecart * i) / n, finPrec + (ecart * (i + 1)) / n);
    }
    for (let i = 0; i < str.length; i++) ajoute(str[i], x + i * car, x + (i + 1) * car);
    finPrec = x + str.length * car;
    carPrec = car;
  }
  return { texte, debuts, fins };
}

/** La ligne prête à lire : un texte brut, ou les morceaux posés par le PDF. */
function prepare(brute) {
  if (typeof brute === 'string') {
    // Un texte brut se lit comme écrit à la machine : chaque caractère a la
    // même largeur, et les colonnes restent alignées.
    const { texte, debuts, fins } = aplatit([
      { str: brute, x: 0, largeur: brute.length * LARGEUR_CAR },
    ]);
    return { texte, debuts, fins };
  }
  const l = brute || {};
  const { texte, debuts, fins } = aplatit(l.morceaux || []);
  return { texte, debuts, fins, y: l.y, hauteur: l.hauteur, page: l.page };
}

/** Remplace un passage par des espaces, pour garder les positions. */
const efface = (s, debut, fin) => s.slice(0, debut) + ' '.repeat(fin - debut) + s.slice(fin);

/** La date ISO d'un « 05.01.26 », ou '' si elle n'existe pas. */
function dateIso(j, m, a) {
  const jour = Number(j);
  const mois = Number(m);
  let an = Number(a);
  if (a.length === 2) an += an < 70 ? 2000 : 1900;
  if (mois < 1 || mois > 12 || jour < 1 || jour > 31) return '';
  return an + '-' + String(mois).padStart(2, '0') + '-' + String(jour).padStart(2, '0');
}

/**
 * Ce que dit une ligne : ses dates de tête, son libellé, ses montants de fin
 * (le mouvement, puis le solde), chacun avec sa position.
 */
function decoupe(ligne) {
  const { texte, debuts, fins } = ligne;
  const pos = (debut, fin) => ({ debut: debuts[debut] ?? 0, fin: fins[fin - 1] ?? 0 });
  let masque = texte;
  const dates = trouveDates(texte);
  for (const d of dates) masque = efface(masque, d.index, d.index + d.longueur);
  const montants = [];
  let reste = masque;
  for (const m of masque.matchAll(RE_MONTANT)) {
    const chiffres = m[2].replace(/\D/g, '') + '.' + m[3];
    const negatif = (!!m[1] && m[1] !== '+') || !!m[4];
    const valeur = (negatif ? -1 : 1) * Number(chiffres);
    const fin = m.index + m[0].length;
    // La devise collée au montant : « EUR 45.00 », « 45.30 CHF », « CHF45.30 ».
    const devise = (masque.slice(Math.max(0, m.index - 5), m.index).match(DEVISE_AVANT) ||
      masque.slice(fin, fin + 5).match(DEVISE_APRES) ||
      [])[1];
    montants.push({ valeur, negatif, index: m.index, devise, ...pos(m.index, fin) });
    reste = efface(reste, m.index, fin);
  }
  reste = reste.replace(RE_DEVISE, (d) => ' '.repeat(d.length));
  // Le libellé va du premier caractère qui n'est pas une date au dernier qui
  // n'est ni un montant, ni une date, ni une devise : les montants qui suivent
  // sont ceux des colonnes ; un nombre dans le libellé (« Facture 2026.01 »)
  // n'en est pas un.
  const debutLibelle = masque.search(/\S/);
  let finLibelle = reste.search(/\S\s*$/);
  // La date d'achat qui finit le libellé (« ACHAT DU 27.08.2026 »), séparée
  // par une seule espace : elle en fait partie, la date de valeur d'une
  // colonne en est loin.
  for (const d of dates) {
    if (debutLibelle < 0 || d.index <= debutLibelle || finLibelle < 0) continue;
    const entre = texte.slice(finLibelle + 1, d.index);
    const avantMontant = !montants.some((m) => m.index > finLibelle && m.index < d.index);
    if (d.index > finLibelle && /^ ?$/.test(entre) && avantMontant)
      finLibelle = d.index + d.longueur - 1;
  }
  const datesDeTete = dates.filter((d) => debutLibelle < 0 || d.index < debutLibelle);
  const libelle =
    debutLibelle >= 0 && finLibelle >= debutLibelle
      ? texte
          .slice(debutLibelle, finLibelle + 1)
          .replace(/\s+/g, ' ')
          .trim()
      : '';
  return {
    dates: datesDeTete,
    libelle,
    xLibelle: debutLibelle >= 0 ? debuts[debutLibelle] : null,
    montants: montants.filter((m) => m.index > finLibelle),
    tousMontants: montants,
  };
}

/** Les colonnes Débit / Crédit / Solde d'une ligne d'en-tête, sinon null. */
function enTete(ligne) {
  const { texte, debuts, fins } = ligne;
  const mots = [...texte.matchAll(/[^\s/|]+/g)];
  // Un en-tête est sans date, et court : une phrase du pied de page qui parle
  // « des débits et des crédits » n'en est pas un. Un en-tête long (« Date de
  // comptabilisation  Texte de l'écriture  Montant au débit… ») a aussi sa
  // colonne de dates ou de solde.
  if (trouveDates(texte).length || mots.length > 20) return null;
  const cols = {};
  const place = (debut, fin) => ({ debut: debuts[debut], fin: fins[fin - 1] });
  const pris = [];
  for (const m of texte.matchAll(COL_DEUX_MOTS)) {
    const genre = m[1].toLowerCase() === 'out' ? 'debit' : 'credit';
    if (!cols[genre]) cols[genre] = place(m.index, m.index + m[0].length);
    pris.push([m.index, m.index + m[0].length]);
  }
  let autre = false;
  for (const m of mots) {
    if (pris.some(([a, b]) => m.index >= a && m.index < b)) continue;
    const mot = m[0].replace(/[.:;,()]+/g, '');
    const ici = place(m.index, m.index + m[0].length);
    if (COL_AUTRE.test(mot)) autre = true;
    if (!cols.debit && COL_DEBIT.test(mot)) cols.debit = ici;
    else if (!cols.credit && COL_CREDIT.test(mot)) cols.credit = ici;
    else if (!cols.solde && COL_SOLDE.test(mot)) cols.solde = ici;
  }
  if (!cols.debit || !cols.credit) return null;
  // Long, il a ses colonnes séparées par un vide, comme un tableau ; une
  // phrase ne laisse qu'une espace entre ses mots.
  if (mots.length > 10 && (!autre || texte.trim().split(/\s{2,}/).length < 4)) return null;
  // Un en-tête n'a pas de montant : une ligne « Total débits 1'200.00
  // crédits 900.00 » n'en est pas un.
  if (decoupe(ligne).tousMontants.length) return null;
  return cols;
}

const centre = (p) => (p.debut + p.fin) / 2;

/** L'écart d'un montant à une colonne : alignées à gauche, à droite ou au centre. */
const ecartA = (m, c) =>
  Math.min(Math.abs(m.debut - c.debut), Math.abs(m.fin - c.fin), Math.abs(centre(m) - centre(c)));

/**
 * Le mouvement et le solde d'une ligne : par colonnes quand l'en-tête est
 * connu, sinon le premier montant est le mouvement et le dernier le solde.
 */
function repartit(montants, colonnes) {
  if (colonnes) {
    // Un montant loin de toutes les colonnes (un en-tête d'un autre tableau)
    // ne se range pas : le signe ou le solde décideront.
    const loin = Math.max(LARGEUR_CAR, Math.abs(centre(colonnes.debit) - centre(colonnes.credit)));
    const genres = montants.map((m, i) => {
      if (!colonnes.solde && montants.length > 1 && i === montants.length - 1) return 'SOLDE';
      const choix = [
        ['DBIT', colonnes.debit],
        ['CRDT', colonnes.credit],
      ];
      if (colonnes.solde) choix.push(['SOLDE', colonnes.solde]);
      let genre = null;
      let meilleur = Infinity;
      for (const [g, c] of choix) {
        const e = ecartA(m, c);
        if (e < meilleur) {
          meilleur = e;
          genre = g;
        }
      }
      return meilleur <= loin ? genre : null;
    });
    const iSolde = genres.lastIndexOf('SOLDE');
    // Le mouvement : d'abord un montant en francs rangé en Débit ou en Crédit.
    const ranges = genres
      .map((g, i) => i)
      .filter((i) => genres[i] === 'DBIT' || genres[i] === 'CRDT');
    const range = ranges.find((i) => !etranger(montants[i])) ?? ranges[0] ?? -1;
    const iMouvement = range >= 0 ? range : genres.findIndex((g) => g === null);
    return {
      mouvement: iMouvement >= 0 ? montants[iMouvement] : null,
      sens: iMouvement >= 0 ? genres[iMouvement] : null,
      solde: iSolde >= 0 ? montants[iSolde].valeur : null,
      enDevise: montants.find((m, i) => i !== iMouvement && etranger(m)) || null,
    };
  }
  // Le mouvement juste avant le solde ; un montant en devise (« EUR 45.00 »)
  // n'est ni l'un ni l'autre : sans colonne de solde, « EUR 45.00   43.15 »
  // est un mouvement de 43.15, pas un solde.
  const enFrancs = montants.filter((m) => !etranger(m));
  const liste = enFrancs.length ? enFrancs : montants;
  return {
    mouvement: liste.length > 1 ? liste[liste.length - 2] : liste[0] || null,
    sens: null,
    solde: liste.length > 1 ? liste[liste.length - 1].valeur : null,
    enDevise: montants.find(etranger) || null,
  };
}

/** Un montant écrit dans une autre devise que le franc. */
const etranger = (m) => !!m?.devise && m.devise !== 'CHF';

/**
 * Une ligne de solde (reporté, final…) ? Une ligne datée qui commence par
 * « Solde » peut être un paiement (« Solde facture 2026-14 ») : elle n'est un
 * solde que sans mouvement à côté — par colonnes, c'est la colonne qui le dit.
 */
function estUnSolde(d, colonnes) {
  if (!RE_SOLDE.test(d.libelle) || !d.tousMontants.length) return false;
  if (!d.dates.length) return true;
  if (colonnes) return false;
  // Datée, sans colonnes : un solde n'a que ses mots (« Solde au 30.09.2026 ») ;
  // « Solde travaux villa Dupont 1'500.00 » est un paiement.
  const mots = d.libelle
    .replace(/\d{1,2}[./]\d{1,2}[./]\d{2,4}/g, ' ')
    .split(/\s+/)
    .filter(Boolean);
  return d.montants.length <= 1 && mots.every((m) => MOTS_DU_SOLDE.test(m));
}

/**
 * La ligne suit-elle la précédente, dans le même mouvement ? Même page, juste
 * en dessous, et commencée dans la colonne du libellé : un pied de page, collé
 * à la marge, n'est pas la suite du dernier mouvement.
 */
function suit(prec, ligne, xLibelle) {
  if (prec.page !== undefined && ligne.page !== undefined && prec.page !== ligne.page) return false;
  if (typeof prec.y === 'number' && typeof ligne.y === 'number') {
    const ecart = prec.y - ligne.y;
    const h = Math.max(prec.hauteur || 0, ligne.hauteur || 0) || 10;
    if (ecart <= 0 || ecart > 2.5 * h) return false;
  }
  if (xLibelle !== null && xLibelle !== undefined) {
    const premier = ligne.texte.search(/\S/);
    if (premier < 0 || ligne.debuts[premier] < xLibelle - 2 * LARGEUR_CAR) return false;
  }
  return true;
}

/** La référence QR (27 chiffres) ou SCOR (RF…) d'un libellé, si elle s'y trouve. */
function reference(texte) {
  for (const m of String(texte).matchAll(/\d[\d ]{25,}\d/g)) {
    // Les groupes de chiffres qui se suivent : la référence peut être collée à
    // un autre nombre (une date, un numéro de client).
    const groupes = m[0].split(' ').filter(Boolean);
    for (let i = 0; i < groupes.length; i++) {
      let chiffres = '';
      for (let j = i; j < groupes.length && chiffres.length < 27; j++) {
        chiffres += groupes[j];
        if (chiffres.length === 27 && _mod10(chiffres.slice(0, 26)) === Number(chiffres[26]))
          return chiffres;
      }
    }
  }
  for (const m of String(texte).matchAll(/\bRF\d{2}(?: ?[A-Z0-9]{1,4}){1,6}/gi)) {
    // Le mot qui suit la référence s'y colle parfois : on raccourcit jusqu'à
    // trouver une clé de contrôle juste.
    const groupes = m[0].toUpperCase().split(' ');
    for (let n = groupes.length; n >= 1; n--) {
      const ref = groupes.slice(0, n).join('');
      if (ref.length < 5 || ref.length > 25) continue;
      const conv = (ref.slice(4) + ref.slice(0, 4)).replace(/[A-Z]/g, (c) =>
        String(c.charCodeAt(0) - 55),
      );
      if (_mod97(conv) === 1) return ref;
    }
  }
  return '';
}

/** Les montants en centimes, pour comparer sans erreur d'arrondi. */
const centimes = (x) => Math.round(x * 100);

/** Inconnues au plus que le solde départage (deux moitiés de 2^20 combinaisons). */
const INCONNUES_MAX = 40;

/**
 * Les signes de montants dont la somme signée fait la cible, s'il n'y a
 * qu'une seule façon d'y arriver : pour chaque montant, vrai = entrée.
 *
 * <p>Les entrées font ensemble (total + cible) / 2 : c'est une somme de
 * sous-ensemble, cherchée en deux moitiés (toutes les sommes de la première,
 * puis, pour chaque somme de la seconde, son complément). Quarante montants
 * se départagent ainsi en deux fois un million de sommes, là où les essayer
 * toutes en demanderait mille milliards.
 *
 * @param {number[]} montants les montants, en centimes, positifs
 * @param {number} cible entrées moins sorties, en centimes
 * @returns {boolean[]|null} les signes, ou null (aucune ou plusieurs façons)
 */
export function signesParLaSomme(montants, cible) {
  const n = montants.length;
  if (!n || n > INCONNUES_MAX) return null;
  const total = montants.reduce((s, m) => s + m, 0);
  if ((total + cible) % 2 !== 0) return null;
  const entrees = (total + cible) / 2;
  if (entrees < 0 || entrees > total) return null;
  const moitie = n >> 1;
  const a = montants.slice(0, moitie);
  const b = montants.slice(moitie);
  // Les sommes de la première moitié : combien de façons (deux suffisent), et
  // la première trouvée.
  const sommesA = new Map();
  const parcours = (liste, visite) => {
    const sommes = new Float64Array(1 << liste.length);
    for (let masque = 1; masque < sommes.length; masque++) {
      const bas = masque & -masque;
      sommes[masque] = sommes[masque ^ bas] + liste[31 - Math.clz32(bas)];
    }
    for (let masque = 0; masque < sommes.length; masque++) {
      if (visite(sommes[masque], masque) === false) return;
    }
  };
  parcours(a, (s, masque) => {
    if (s > entrees) return;
    const deja = sommesA.get(s);
    if (deja) deja.n = Math.min(2, deja.n + 1);
    else sommesA.set(s, { n: 1, masque });
  });
  let trouvees = 0;
  let solution = null;
  parcours(b, (s, masque) => {
    const comp = sommesA.get(entrees - s);
    if (!comp) return true;
    trouvees += comp.n;
    solution = [comp.masque, masque];
    return trouvees < 2;
  });
  if (trouvees !== 1) return null;
  return montants.map((_, i) =>
    i < moitie ? !!(solution[0] & (1 << i)) : !!(solution[1] & (1 << (i - moitie))),
  );
}

/**
 * Les sens qui manquent, trouvés par le solde : entre deux soldes connus, la
 * seule combinaison d'entrées et de sorties qui passe de l'un à l'autre. Une
 * banque n'imprime parfois le solde qu'en fin de journée, ou en fin de mois.
 */
function parLeSolde(groupe, avants, apres) {
  if (apres === null) return;
  // Les colonnes contredites par le solde (un en-tête mal aligné) : le solde
  // l'emporte, s'il ne laisse qu'une façon de passer de l'un à l'autre.
  const net = groupe.reduce(
    (s, t) => (t.sens ? s + (t.sens === 'CRDT' ? 1 : -1) * centimes(t.montant) : s),
    0,
  );
  const tombeJuste = avants.some((a) => centimes(a) + net === centimes(apres));
  if (
    avants.length &&
    !tombeJuste &&
    groupe.every((t) => t.sens) &&
    groupe.some((t) => t.parColonne)
  ) {
    const avant = groupe.map((t) => t.sens);
    groupe.forEach((t) => {
      if (t.parColonne) t.sens = null;
    });
    parLeSolde(groupe, avants, apres);
    if (groupe.some((t) => !t.sens)) groupe.forEach((t, i) => (t.sens = avant[i]));
    return;
  }
  const inconnus = groupe.filter((t) => !t.sens);
  if (!inconnus.length) return;
  const fixe = groupe
    .filter((t) => t.sens)
    .reduce((s, t) => s + (t.sens === 'CRDT' ? 1 : -1) * centimes(t.montant), 0);
  // Avant le premier mouvement, plusieurs soldes peuvent précéder (ancien et
  // nouveau solde du résumé) : le plus proche d'abord.
  for (const avant of avants.slice().reverse()) {
    const signes = signesParLaSomme(
      inconnus.map((t) => centimes(t.montant)),
      centimes(apres) - centimes(avant) - fixe,
    );
    if (signes) {
      inconnus.forEach((t, i) => {
        t.sens = signes[i] ? 'CRDT' : 'DBIT';
      });
      return;
    }
  }
}

/**
 * Lit un relevé depuis ses lignes de texte : ses mouvements, et ses soldes
 * d'ouverture et de clôture quand il les imprime (pour contrôler que solde
 * initial + entrées − sorties = solde final).
 *
 * @param {Array<string|{morceaux: Array<{str: string, x: number, largeur?: number}>,
 *   y?: number, hauteur?: number, page?: number}>} lignes les lignes du relevé,
 *   en texte brut ou avec la position de chaque morceau (PDF)
 * @returns {{transactions: Array<{date: string, montant: number,
 *   sens: 'CRDT'|'DBIT'|'?', label: string, ref: string}>, soldeInitial?: number,
 *   soldeFinal?: number}} le relevé
 */
export function lisLeTexteDuReleve(lignes) {
  const suite = []; // mouvements et soldes, dans l'ordre
  let colonnes = null;
  let courant = null; // le mouvement auquel la ligne suivante peut s'ajouter
  let prec = null; // la dernière ligne de ce mouvement
  let suites = 0;
  let page;
  // Le mouvement de la fin de la page précédente : son libellé peut continuer
  // en haut de la page suivante, sous l'en-tête répété.
  let enAttente = null;
  // Les montants déjà vus sur les lignes datées : la colonne où ils tombent.
  const vus = [];
  const aligne = (montants) =>
    !vus.length || vus.some((v) => ecartA(montants[montants.length - 1], v) <= 2 * LARGEUR_CAR);
  for (const brute of lignes || []) {
    const ligne = prepare(brute);
    if (!ligne.texte.trim()) continue;
    if (ligne.page !== undefined && ligne.page !== page) {
      page = ligne.page;
      enAttente = courant && courant.xLibelle !== null ? { e: courant, n: 0 } : null;
      courant = null;
    }
    const cols = enTete(ligne);
    if (cols) {
      colonnes = cols;
      courant = null;
      continue;
    }
    const d = decoupe(ligne);
    if (estUnSolde(d, colonnes)) {
      suite.push({ solde: d.tousMontants[d.tousMontants.length - 1].valeur });
      courant = null;
      continue;
    }
    // Un total de page n'a pas de date ; « 05.09.2026 Total Station-service »
    // est un plein d'essence.
    if ((RE_TOTAL.test(d.libelle) && !d.dates.length) || RE_PAGE.test(d.libelle)) {
      courant = null;
      continue;
    }
    if (d.dates.length) {
      enAttente = null;
      vus.push(...d.montants);
      courant = {
        date: d.dates[0].iso,
        libelles: d.libelle ? [d.libelle] : [],
        montants: d.montants,
        colonnes,
        xLibelle: d.libelle ? d.xLibelle : null,
      };
      suite.push(courant);
      prec = ligne;
      suites = 0;
      continue;
    }
    if (!courant && enAttente) {
      // En haut de la page : la suite du libellé, commencée dans sa colonne,
      // sans montant. Le reste (nom de la banque, adresse) est ailleurs.
      const premier = ligne.texte.search(/\S/);
      const x = ligne.debuts[premier];
      if (
        !d.tousMontants.length &&
        d.libelle &&
        Math.abs(x - enAttente.e.xLibelle) <= 2 * LARGEUR_CAR &&
        enAttente.n < SUITES_MAX
      ) {
        enAttente.e.libelles.push(d.libelle);
        enAttente.n += 1;
      } else if (d.tousMontants.length) enAttente = null;
      continue;
    }
    if (!courant || !suit(prec, ligne, courant.xLibelle)) {
      courant = null;
      continue;
    }
    if (d.montants.length && !aligne(d.montants)) {
      // Un nombre au bout du libellé (« Facture 2026.01 »), hors des colonnes
      // des montants : il fait partie du texte.
      d.libelle = ligne.texte.replace(/\s+/g, ' ').trim();
      d.montants = [];
    }
    if (d.montants.length && !courant.montants.length) {
      // Le montant sous la date, à la fin du libellé.
      if (d.libelle) courant.libelles.push(d.libelle);
      courant.montants = d.montants;
      vus.push(...d.montants);
    } else if (d.montants.length && d.libelle) {
      // Un nouveau mouvement du même jour : la banque n'a pas répété la date.
      courant = {
        date: courant.date,
        libelles: [d.libelle],
        montants: d.montants,
        colonnes,
        xLibelle: courant.xLibelle,
      };
      suite.push(courant);
      suites = 0;
    } else if (!d.montants.length && d.libelle && suites < SUITES_MAX) {
      courant.libelles.push(d.libelle);
      suites += 1;
    } else {
      courant = null;
      continue;
    }
    prec = ligne;
  }

  // Le mouvement et le solde de chaque ligne ; une ligne qui n'a qu'un solde
  // (dans la colonne Solde) n'est pas un mouvement.
  const elements = [];
  for (const e of suite) {
    if (e.solde !== undefined) {
      elements.push(e);
      continue;
    }
    if (!e.montants.length) continue;
    const r = repartit(e.montants, e.colonnes);
    if (!r.mouvement) {
      if (r.solde !== null) elements.push({ solde: r.solde });
      continue;
    }
    if (Math.abs(r.mouvement.valeur) < 0.005) continue;
    let label = e.libelles.join(' ').replace(/\s+/g, ' ').trim();
    // Le montant payé en devise reste lisible : il relie le ticket en euros.
    if (r.enDevise) {
      const devise = r.enDevise.devise + ' ' + Math.abs(r.enDevise.valeur).toFixed(2);
      if (!label.includes(devise)) label = (label + ' ' + devise).trim();
    }
    elements.push({
      date: e.date,
      montant: Math.round(Math.abs(r.mouvement.valeur) * 100) / 100,
      sens: r.sens,
      parColonne: !!r.sens,
      label,
      ref: reference(label),
      negatif: r.mouvement.negatif,
      position: r.mouvement,
      soldeApres: r.solde,
    });
  }
  const mouvements = elements.filter((e) => e.solde === undefined);

  // Le signe : un relevé qui marque ses sorties d'un « - » ne marque pas ses
  // entrées.
  const signes = mouvements.some((t) => !t.sens && t.negatif);
  if (signes) mouvements.forEach((t) => (t.sens = t.sens || (t.negatif ? 'DBIT' : 'CRDT')));

  // Le solde : d'un solde connu au suivant.
  let avants = [];
  let groupe = [];
  let dejaUnMouvement = false;
  for (const e of elements) {
    if (e.solde !== undefined) {
      if (groupe.length) parLeSolde(groupe, avants, e.solde);
      avants = dejaUnMouvement ? [e.solde] : avants.concat(e.solde);
      groupe = [];
      continue;
    }
    dejaUnMouvement = true;
    groupe.push(e);
    if (e.soldeApres !== null) {
      parLeSolde(groupe, avants, e.soldeApres);
      avants = [e.soldeApres];
      groupe = [];
    }
  }

  // Ce qui reste : la colonne des sens déjà trouvés (un relevé sans en-tête
  // lisible garde ses colonnes), puis les mots du libellé, puis la colonne
  // des sens que les mots ont donnés.
  const parLaColonne = () => {
    const connus = mouvements.filter((t) => t.sens);
    const plusPres = (t, sens) =>
      Math.min(
        Infinity,
        ...connus.filter((k) => k.sens === sens).map((k) => ecartA(t.position, k.position)),
      );
    for (const t of mouvements) {
      if (t.sens) continue;
      const aD = plusPres(t, 'DBIT');
      const aC = plusPres(t, 'CRDT');
      if (Number.isFinite(aD) && Number.isFinite(aC) && Math.abs(aD - aC) > LARGEUR_CAR)
        t.sens = aD < aC ? 'DBIT' : 'CRDT';
    }
  };
  parLaColonne();
  for (const t of mouvements) if (!t.sens) t.sens = sensDesMots(t.label);
  parLaColonne();
  // Sans indice, ni sortie ni entrée : une entrée inventée marquerait une
  // facture payée à tort, une sortie inventée une dépense qui n'existe pas.
  for (const t of mouvements) if (!t.sens) t.sens = SENS_A_VERIFIER;

  return {
    transactions: mouvements.map((t) => ({
      date: t.date,
      montant: t.montant,
      sens: t.sens,
      label: t.label,
      ref: t.ref,
    })),
    ...soldesDuTexte(elements),
  };
}

/** Le montant signé d'un mouvement (entrée positive), ou null si son sens manque. */
const signe = (t) => (t.sens === 'CRDT' ? t.montant : t.sens === 'DBIT' ? -t.montant : null);

/**
 * Le solde d'ouverture et de clôture d'un relevé lu : le solde imprimé avant
 * le premier mouvement (ou celui d'après le premier, moins ce mouvement) et
 * celui d'après le dernier. Parmi plusieurs soldes possibles (le résumé en
 * tête imprime l'ancien et le nouveau), ceux qui tombent juste d'abord.
 */
function soldesDuTexte(elements) {
  const iPremier = elements.findIndex((e) => e.solde === undefined);
  if (iPremier < 0) return {};
  let iDernier = elements.length - 1;
  while (elements[iDernier].solde !== undefined) iDernier--;
  const premier = elements[iPremier];
  const dernier = elements[iDernier];
  const avants = elements
    .slice(0, iPremier)
    .map((e) => e.solde)
    .reverse();
  if (!avants.length && premier.soldeApres !== null && signe(premier) !== null)
    avants.push(premier.soldeApres - signe(premier));
  const apres = elements.slice(iDernier + 1).map((e) => e.solde);
  if (dernier.soldeApres !== null) apres.unshift(dernier.soldeApres);
  if (!avants.length || !apres.length) return {};
  const mouvements = elements.slice(iPremier, iDernier + 1).filter((e) => e.solde === undefined);
  const net = mouvements.every((t) => signe(t) !== null)
    ? mouvements.reduce((s, t) => s + centimes(signe(t)), 0)
    : null;
  for (const a of avants) {
    for (const b of apres) {
      if (net !== null && centimes(a) + net === centimes(b))
        return { soldeInitial: a, soldeFinal: b };
    }
  }
  return { soldeInitial: avants[0], soldeFinal: apres[0] };
}

/**
 * Les mouvements d'un relevé, depuis ses lignes de texte.
 *
 * @param {Array<string|object>} lignes les lignes du relevé ({@link lisLeTexteDuReleve})
 * @returns {Array<{date: string, montant: number, sens: 'CRDT'|'DBIT'|'?', label: string,
 *   ref: string}>} les mouvements, dans l'ordre du relevé
 */
export function transactionsDuTexte(lignes) {
  return lisLeTexteDuReleve(lignes).transactions;
}

/**
 * Les lignes d'une page de PDF : les morceaux de texte de même hauteur
 * réunis, de gauche à droite, de haut en bas.
 *
 * @param {Array<{str: string, transform: number[], width?: number, height?: number}>} items
 *   les morceaux donnés par PDF.js (getTextContent)
 * @param {number} page le numéro de la page
 * @returns {Array<{morceaux: Array, y: number, hauteur: number, page: number}>} les lignes
 */
export function lignesDeLaPage(items, page) {
  const morceaux = [];
  for (const it of items || []) {
    const str = it && typeof it.str === 'string' ? it.str : '';
    if (!str.trim()) continue;
    const t = it.transform || [1, 0, 0, 1, 0, 0];
    const hauteur = Math.abs(it.height) || Math.hypot(t[2], t[3]) || 10;
    morceaux.push({ str, x: t[4], y: t[5], largeur: it.width || 0, hauteur });
  }
  // L'origine d'une page PDF est en bas : le haut de la page a le plus grand y.
  morceaux.sort((a, b) => b.y - a.y || a.x - b.x);
  const lignes = [];
  for (const m of morceaux) {
    const l = lignes[lignes.length - 1];
    if (l && Math.abs(l.y - m.y) <= Math.max(2, Math.min(l.hauteur, m.hauteur) * 0.5)) {
      l.morceaux.push(m);
      l.hauteur = Math.max(l.hauteur, m.hauteur);
    } else lignes.push({ y: m.y, hauteur: m.hauteur, page, morceaux: [m] });
  }
  for (const l of lignes) l.morceaux.sort((a, b) => a.x - b.x);
  return lignes;
}

/**
 * Lit un relevé bancaire en PDF.
 *
 * @param {Blob} fichier le PDF du relevé
 * @returns {Promise<{avecTexte: boolean, transactions: Array, soldeInitial?: number,
 *   soldeFinal?: number, banque: string}>} les mouvements et les soldes
 *   imprimés ; `avecTexte` faux pour un PDF scanné, qui n'a pas de texte à
 *   lire ; `banque` celle que l'en-tête de la première page nomme, ou ''
 */
export async function lisRelevePdf(fichier) {
  const [pdfjs, { default: Travailleur }] = await Promise.all([
    import('pdfjs-dist/legacy/build/pdf.mjs'),
    import('pdfjs-dist/legacy/build/pdf.worker.min.mjs?worker'),
  ]);
  if (!pdfjs.GlobalWorkerOptions.workerPort) {
    pdfjs.GlobalWorkerOptions.workerPort = new Travailleur();
  }
  const tache = pdfjs.getDocument({
    data: new Uint8Array(await fichier.arrayBuffer()),
    isEvalSupported: false,
  });
  try {
    const doc = await tache.promise;
    const lignes = [];
    for (let n = 1; n <= Math.min(doc.numPages, PAGES_MAX); n += 1) {
      const page = await doc.getPage(n);
      const contenu = await page.getTextContent();
      lignes.push(...lignesDeLaPage(contenu.items, n));
    }
    const signes = lignes.reduce(
      (s, l) => s + l.morceaux.reduce((t, m) => t + m.str.replace(/\s/g, '').length, 0),
      0,
    );
    // Un PDF scanné n'a pas de texte, ou presque (un tampon, un numéro).
    if (signes < 20) return { avecTexte: false, transactions: [], banque: '' };
    // La banque se lit dans l'en-tête : la première page, de haut en bas.
    const premiere = lignes
      .filter((l) => l.page === 1)
      .map((l) => l.morceaux.map((m) => m.str).join(' '))
      .join('\n');
    return { avecTexte: true, ...lisLeTexteDuReleve(lignes), banque: banqueDuTexte(premiere) };
  } finally {
    tache.destroy().catch(() => {});
  }
}
